/**
 * CallModePanel — the right-side slide-in "Call Mode" co-pilot.
 *
 * Fires when the operator taps "Start Call" on any Deal Card. Renders:
 *   1. Guided Seller Call — 5 editable steps, checkboxable, auto-save
 *   2. Live Deal Analyzer — ARV / rehab / MAO / wholetail side-panel that
 *      recomputes on every input change (via the Critical Systems endpoint)
 *   3. End-call sheet — outcome selector → summary preview → drops to timeline
 *
 * Backend surface used:
 *   GET   /v3/call-mode/script                    (defaults or user's custom)
 *   POST  /v3/call-mode/session/:pid              (start)
 *   PATCH /v3/call-mode/session/:sid/step         (per-step autosave)
 *   POST  /v3/call-mode/session/:sid/end          (finalize + timeline)
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  PhoneOff, Phone, X, Loader2, Copy, Check, ChevronRight, SkipForward,
  CircleDot, CheckCircle2, DollarSign, TrendingUp, Sparkles, ExternalLink,
} from "lucide-react";
import CriticalSystemsCheck from "./CriticalSystemsCheck";

const fmtMoney = (n) =>
  n == null ? "—" : `$${Math.round(Number(n)).toLocaleString()}`;

// Simple template render: replaces {token} with property/session state.
function fillPrompt(template, ctx) {
  return String(template || "").replace(/\{(\w+)\}/g, (_, k) => ctx[k] ?? `{${k}}`);
}

export default function CallModePanel({ property, open, onClose, onMetricsUpdate }) {
  const [script, setScript]       = useState(null);
  const [session, setSession]     = useState(null);
  const [answers, setAnswers]     = useState({});   // {step_key: {answer, skipped}}
  const [active, setActive]       = useState(0);
  const [metrics, setMetrics]     = useState(null);
  const [ending, setEnding]       = useState(false);
  const [endOpen, setEndOpen]     = useState(false);
  const [outcome, setOutcome]     = useState("follow_up");
  const [finalPrice, setFinal]    = useState("");
  const [endNotes, setEndNotes]   = useState("");
  const [copied, setCopied]       = useState(false);
  const saveTimers = useRef({});

  // ── Boot: load script + start session when the panel opens ─────────
  useEffect(() => {
    if (!open || !property?.id) return;
    let alive = true;
    (async () => {
      try {
        const s  = await api.get("/v3/call-mode/script");
        if (!alive) return;
        setScript(s.data.steps || []);
        const sess = await api.post(`/v3/call-mode/session/${property.id}`);
        if (!alive) return;
        setSession(sess.data.session);
        setAnswers({});
        setActive(0);
        // Warm metrics from the property doc itself — no extra fetch needed.
        setMetrics(property.metrics || null);
      } catch {
        toast.error("Couldn't start Call Mode");
      }
    })();
    return () => { alive = false; };
  }, [open, property?.id]);   // eslint-disable-line

  // ── Autosave a step's answer (debounced) ───────────────────────────
  const saveStep = useCallback((step_key, next) => {
    setAnswers((cur) => ({ ...cur, [step_key]: { ...cur[step_key], ...next } }));
    if (!session?.id) return;
    clearTimeout(saveTimers.current[step_key]);
    saveTimers.current[step_key] = setTimeout(async () => {
      const payload = {
        step_key,
        answer:  next.answer  ?? "",
        skipped: !!next.skipped,
      };
      try { await api.patch(`/v3/call-mode/session/${session.id}/step`, payload); }
      catch { /* silent — user can retry */ }
    }, 600);
  }, [session?.id]);

  // ── Copy the current step's prompt ─────────────────────────────────
  const copyPrompt = useCallback((text) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  }, []);

  // ── End the call ───────────────────────────────────────────────────
  const endCall = useCallback(async () => {
    if (!session?.id) return;
    setEnding(true);
    try {
      const { data } = await api.post(`/v3/call-mode/session/${session.id}/end`, {
        outcome,
        final_price: finalPrice ? Number(finalPrice) : null,
        notes: endNotes,
      });
      toast.success("Call logged — summary saved to timeline");
      onClose?.({ summary: data.summary, outcome });
      setEndOpen(false);
    } catch (e) {
      toast.error("Couldn't finalize the call");
    } finally { setEnding(false); }
  }, [session?.id, outcome, finalPrice, endNotes, onClose]);

  const ctx = useMemo(() => ({
    operator_name:       property?.operator_name || "your name",
    seller_first_name:   property?.seller_first_name || "there",
    property_address:    property?.address_line1 || property?.address || "your property",
    their_price:         answers?.price?.answer || "your number",
  }), [property, answers]);

  if (!open) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-[80] bg-slate-900/40 backdrop-blur-[2px]"
        onClick={() => setEndOpen(true)}
        data-testid="callmode-backdrop"
      />
      {/* Panel */}
      <aside
        className="fixed top-0 right-0 z-[81] h-full w-full sm:w-[720px] bg-white shadow-2xl flex flex-col"
        data-testid="callmode-panel"
      >
        {/* Header */}
        <header className="px-5 py-4 border-b border-slate-200 bg-gradient-to-br from-indigo-600 to-indigo-800 text-white flex items-center gap-3">
          <span className="h-9 w-9 rounded-full bg-white/15 grid place-items-center">
            <Phone className="w-4 h-4" />
          </span>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] font-black uppercase tracking-[0.18em] opacity-80">
              Call Mode · Guided Seller Call
            </div>
            <div className="text-[14px] font-black truncate">
              {property?.address_line1 || property?.address || "Current deal"}
            </div>
          </div>
          <button
            type="button"
            onClick={() => setEndOpen(true)}
            className="rounded-lg bg-rose-500 hover:bg-rose-600 text-white text-[12.5px] font-bold px-3 py-1.5 inline-flex items-center gap-1.5"
            data-testid="callmode-end"
          >
            <PhoneOff className="w-3.5 h-3.5" /> End call
          </button>
        </header>

        {/* Body — 2 columns: steps + live analyzer */}
        <div className="flex-1 overflow-y-auto">
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_260px] gap-0">
            {/* Steps column */}
            <div className="p-5 space-y-3">
              {script?.length ? script.map((step, i) => {
                const isActive = active === i;
                const state = answers[step.key] || {};
                const done  = !!(state.answer || state.skipped);
                return (
                  <div
                    key={step.key}
                    className={`rounded-2xl border ${isActive ? "border-indigo-400 shadow-md" : "border-slate-200"} bg-white overflow-hidden`}
                    data-testid={`callmode-step-${step.key}`}
                  >
                    {/* Step head */}
                    <button
                      type="button"
                      onClick={() => setActive(i)}
                      className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-slate-50"
                    >
                      <span
                        className={`h-8 w-8 rounded-full grid place-items-center text-[12px] font-black shrink-0 ${
                          done ? "bg-emerald-100 text-emerald-700" :
                          isActive ? "bg-indigo-100 text-indigo-700" :
                          "bg-slate-100 text-slate-500"
                        }`}
                      >
                        {done ? <CheckCircle2 className="w-4 h-4" /> : i + 1}
                      </span>
                      <div className="flex-1 min-w-0">
                        <div className="text-[13px] font-black text-slate-900">{step.label}</div>
                        {state.skipped && (
                          <div className="text-[10.5px] font-bold uppercase tracking-widest text-slate-400">Skipped</div>
                        )}
                      </div>
                      <ChevronRight className={`w-4 h-4 text-slate-400 transition-transform ${isActive ? "rotate-90" : ""}`} />
                    </button>

                    {/* Step body */}
                    {isActive && (
                      <div className="px-4 pb-4 pt-1 border-t border-slate-100">
                        <div className="rounded-lg bg-indigo-50 border border-indigo-100 p-3 mb-2 text-[13px] text-slate-800 leading-relaxed">
                          {fillPrompt(step.prompt, ctx)}
                        </div>
                        {step.helper && (
                          <div className="text-[11.5px] text-slate-500 mb-2 leading-relaxed">
                            <Sparkles className="w-3 h-3 inline-block mr-1 text-indigo-500" />
                            {step.helper}
                          </div>
                        )}
                        <textarea
                          value={state.answer || ""}
                          onChange={(e) => saveStep(step.key, { answer: e.target.value, skipped: false })}
                          rows={2}
                          placeholder={`Their answer — ${step.label.toLowerCase()}…`}
                          className="w-full text-[13px] border border-slate-200 rounded-lg p-2.5 focus:outline-none focus:border-indigo-400 resize-none"
                          data-testid={`callmode-step-${step.key}-answer`}
                        />
                        <div className="mt-2 flex items-center gap-2">
                          <button
                            type="button"
                            onClick={() => copyPrompt(fillPrompt(step.prompt, ctx))}
                            className="inline-flex items-center gap-1 text-[11.5px] font-semibold text-slate-500 hover:text-indigo-600"
                            data-testid={`callmode-step-${step.key}-copy`}
                          >
                            {copied ? <Check className="w-3 h-3 text-emerald-500" /> : <Copy className="w-3 h-3" />}
                            Copy prompt
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              saveStep(step.key, { answer: state.answer || "", skipped: true });
                              setActive((n) => Math.min(n + 1, (script?.length || 1) - 1));
                            }}
                            className="ml-auto inline-flex items-center gap-1 text-[11.5px] font-semibold text-slate-500 hover:text-slate-700"
                            data-testid={`callmode-step-${step.key}-skip`}
                          >
                            <SkipForward className="w-3 h-3" /> Skip
                          </button>
                          <button
                            type="button"
                            onClick={() => setActive((n) => Math.min(n + 1, (script?.length || 1) - 1))}
                            className="inline-flex items-center gap-1 text-[11.5px] font-bold text-white bg-indigo-600 hover:bg-indigo-700 rounded px-2.5 py-1"
                            data-testid={`callmode-step-${step.key}-next`}
                          >
                            Next <ChevronRight className="w-3 h-3" />
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              }) : (
                <div className="h-64 grid place-items-center">
                  <Loader2 className="w-5 h-5 text-slate-400 animate-spin" />
                </div>
              )}
              <div className="pt-1 text-center">
                <a
                  href="/v3/settings/call-script"
                  className="text-[11.5px] font-semibold text-indigo-600 hover:text-indigo-800 inline-flex items-center gap-1"
                  data-testid="callmode-edit-script"
                >
                  Edit my script <ExternalLink className="w-3 h-3" />
                </a>
              </div>
            </div>

            {/* Live Deal Analyzer sidebar */}
            <aside className="border-l border-slate-200 bg-slate-50 p-4 space-y-3" data-testid="callmode-analyzer">
              <div className="text-[10px] font-black uppercase tracking-[0.18em] text-slate-500">
                Live Deal Analyzer
              </div>
              <AnalyzerTile label="ARV"           value={fmtMoney(metrics?.inputs?.arv)}       icon={TrendingUp} role="cold" />
              <AnalyzerTile label="Repairs"       value={fmtMoney(metrics?.inputs?.repairs)}   icon={CircleDot}  role="warm" />
              <AnalyzerTile label="Flip 70 MAO"   value={fmtMoney(metrics?.flip_70?.mao)}      icon={DollarSign} role="hot" />
              <AnalyzerTile label="Wholesale MAO" value={fmtMoney(metrics?.wholesale?.mao)}    icon={DollarSign} role="hot"    highlight />
              <AnalyzerTile label="Wholetail MAO" value={fmtMoney(metrics?.wholetail?.mao)}    icon={DollarSign} role="won" />
              <div className="pt-2">
                <CriticalSystemsCheck
                  propertyId={property?.id}
                  initial={metrics?.critical_systems}
                  compact
                  onChange={(m) => { setMetrics(m); onMetricsUpdate?.(m); }}
                />
              </div>
            </aside>
          </div>
        </div>

        {/* End-call sheet */}
        {endOpen && (
          <div
            className="absolute inset-0 bg-white/95 backdrop-blur-sm p-6 flex flex-col justify-center"
            data-testid="callmode-end-sheet"
          >
            <div className="max-w-md mx-auto w-full">
              <div className="text-[11px] font-black uppercase tracking-[0.18em] text-indigo-600 mb-1">
                End call · save summary
              </div>
              <h2 className="text-2xl font-black text-slate-900 mb-4">How did it land?</h2>
              <div className="grid grid-cols-2 gap-2 mb-4">
                {[
                  { k: "contract",  label: "Ready to sign",  role: "won"  },
                  { k: "follow_up", label: "Call back later", role: "warm" },
                  { k: "dead",      label: "Not a deal",      role: "risk" },
                  { k: "unknown",   label: "Other",           role: "info" },
                ].map((o) => (
                  <button
                    key={o.k}
                    type="button"
                    onClick={() => setOutcome(o.k)}
                    className={`rounded-lg border text-[13px] font-bold px-3 py-2.5 text-left ${
                      outcome === o.k
                        ? "border-indigo-500 bg-indigo-50 text-indigo-800"
                        : "border-slate-200 hover:border-slate-300 text-slate-700"
                    }`}
                    data-testid={`callmode-outcome-${o.k}`}
                  >
                    {o.label}
                  </button>
                ))}
              </div>
              <input
                type="number"
                value={finalPrice}
                onChange={(e) => setFinal(e.target.value)}
                placeholder="Final price discussed (optional)"
                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm mb-2 focus:outline-none focus:border-indigo-400"
                data-testid="callmode-end-price"
              />
              <textarea
                value={endNotes}
                onChange={(e) => setEndNotes(e.target.value)}
                rows={2}
                placeholder="Notes (optional)"
                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm mb-4 focus:outline-none focus:border-indigo-400 resize-none"
                data-testid="callmode-end-notes"
              />
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setEndOpen(false)}
                  className="text-sm text-slate-500 hover:text-slate-700 px-3 py-2"
                  data-testid="callmode-end-back"
                >
                  Back to call
                </button>
                <button
                  type="button"
                  onClick={endCall}
                  disabled={ending}
                  className="ml-auto inline-flex items-center gap-1.5 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white font-bold text-sm px-4 py-2.5 disabled:opacity-50"
                  data-testid="callmode-end-confirm"
                >
                  {ending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <>Save + close <Check className="w-3.5 h-3.5" /></>}
                </button>
              </div>
            </div>
          </div>
        )}
      </aside>
    </>
  );
}

function AnalyzerTile({ label, value, icon: Icon, role, highlight }) {
  return (
    <div
      className={`rounded-xl px-3 py-2.5 flex items-center gap-2 ${
        highlight
          ? "bg-indigo-600 text-white shadow-md"
          : "bg-white border border-slate-200"
      }`}
      data-testid={`callmode-analyzer-${label.toLowerCase().replace(/\s+/g, "-")}`}
    >
      <Icon className={`w-3.5 h-3.5 ${highlight ? "text-white/80" : "text-slate-400"}`} />
      <div className="flex-1 min-w-0">
        <div className={`text-[10px] font-black uppercase tracking-widest ${highlight ? "text-white/70" : "text-slate-500"}`}>
          {label}
        </div>
        <div className={`text-[15px] font-black tabular-nums ${highlight ? "text-white" : "text-slate-900"}`}>
          {value}
        </div>
      </div>
    </div>
  );
}
